import { FC, memo, ReactElement, Suspense, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'antd-mobile';
import TabsProps from 'antd-mobile/lib/tabs/PropsType';
import cc from 'classcat';
import { TabBarPropsType } from 'rmc-tabs';
import { getType } from 'utils/betslip/betslip';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { BetsProps, EBetslipBetType, EBetslipType } from 'interfaces/betslip';
import { toggleBetslip } from 'newelements/_Betslip/managers/ToggleManager/toggle-betslip-options';
import { useBetslipConfigsContext } from 'newelements/_Betslip/providers/configs-provider';
import { BetslipPreloadState } from 'newelements/_Betslip/Skeleton';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { CustomTabBar } from 'components/CustomTabBar';
import { GlobalIcon } from 'components/GlobalIcon';
import { Loading } from 'components/Loading';
import {
  batchActions,
  setActiveTabPage,
  setMustClose,
  setMustOpen,
  setQuickSingleBetslipOpen
} from 'store/actions';
import {
  getActiveTabPage,
  getAllLsBets,
  getBetslipType,
  getEditBet,
  getIsCounterOfferActive,
  getOpenBetsCount,
  getSingleReturn,
  getSubscribedEvents,
  getTabs,
  getType as getTypeSelector
} from 'store/selectors/betslip';

const { Bets } = lazy(
  () =>
    import(
      /* webpackChunkName: "betslip-bets" */ 'newelements/_Betslip/components/shared/Bets'
    )
);

const { OpenBets } = lazy(
  () =>
    import(
      /* webpackChunkName: "betslip-open-bets" */ 'newelements/_Betslip/views/OpenBets'
    )
);

const { BetslipTabsWrapper } = lazy(
  () =>
    import(
      /* webpackChunkName: "betslip-tabs-wrapper" */ 'newelements/_Betslip/components/wrappers/BetslipTabsWrapper'
    )
);

// @Todo move into types file betslip-types
interface CustomTabProps extends TabBarPropsType {
  fixedTop?: number;
  bordered?: boolean;
  fullwidth?: boolean;
  size?: 'default' | 'middle' | 'large';
  editBet?: boolean;
  allLsBetsCount?: number;
  fromMenu?: boolean;
}

export const BetslipTabs: FC = memo((): ReactElement => {
  const dispatch = useDispatch();

  const { floatingMode } = useBetslipConfigsContext().static;

  const tabs = useSelector(getTabs);
  const type = useSelector(getTypeSelector);
  const page = useSelector(getActiveTabPage);
  const betslipType = useSelector(getBetslipType);
  const singleReturn = useSelector(getSingleReturn);
  const openBetsCount = useSelector(getOpenBetsCount);
  const allLsBetsCount = useSelector(getAllLsBets).length;
  const isEditBEtActive = !!useSelector(getEditBet)?.active;
  const subscribedEvents = useSelector(getSubscribedEvents);
  const isCounterOfferActive = useSelector(getIsCounterOfferActive);

  // @Todo move this into config provider after useEventBus changing
  const currentType =
    betslipType === EBetslipType.BASIC
      ? getType(type, isCounterOfferActive)
      : EBetslipBetType.SINGLE;

  // @Todo need to minimize props
  const renderTabBar = useCallback(
    (props: CustomTabProps) => {
      return (
        <div
          className={cc([
            'betslipTabs__header',
            {
              'betslipTabs__header--paddingLeft':
                isMobile() && props.tabs.length === 1
            }
          ])}
        >
          <Suspense fallback={null}>
            <CustomTabBar
              {...props}
              editBet={isEditBEtActive}
              allLsBetsCount={allLsBetsCount}
              size="middle"
            />
          </Suspense>
          {(isMobile() || floatingMode) && (
            <GlobalIcon
              lib="generic"
              name="close"
              theme="default"
              size={12}
              className="betslip__close-icon"
              onClick={() => {
                toggleBetslip(false);
                dispatch(
                  batchActions(
                    setMustOpen(false),
                    setMustClose(true),
                    setQuickSingleBetslipOpen(false)
                  )
                );
              }}
            />
          )}
        </div>
      );
    },
    [isEditBEtActive, allLsBetsCount, openBetsCount, floatingMode]
  );

  // need to memorize
  const tabProps = useMemo<TabsProps>(
    () => ({
      tabs,
      prerenderingSiblingsNumber: 0,
      destroyInactiveTab: false,
      swipeable: false,
      animated: false,
      renderTabBar,
      page,
      initialPage: Number(!allLsBetsCount && !!openBetsCount),
      onChange: tab => {
        dispatch(setActiveTabPage(tab.id));
      }
    }),
    [tabs, renderTabBar, allLsBetsCount, openBetsCount, page]
  );

  const isBetTypeMultipleOrChain = [
    EBetslipBetType.MULTIPLE,
    EBetslipBetType.CHAIN
  ].includes(currentType);

  const betsProps: BetsProps = useMemo(
    () => ({
      subscribedEvents,
      toggleBetslipView: value => {
        const timeout = 2500;

        if (floatingMode) {
          setTimeout(() => toggleBetslip(value), timeout);
        } else {
          toggleBetslip(value);
        }
      },
      singleReturn,
      currentType,
      isBetTypeMultipleOrChain
    }),
    [subscribedEvents, singleReturn, currentType, isBetTypeMultipleOrChain]
  );

  const Fallback = useCallback(
    () => (isMobile() ? <Loading /> : <BetslipPreloadState />),
    []
  );

  return (
    <ConditionalWrapper
      condition={isMobile() && !SpringConfigs.MOCKED_DATA}
      wrapper={children => (
        <Suspense>
          <BetslipTabsWrapper>{children}</BetslipTabsWrapper>
        </Suspense>
      )}
    >
      {/*@ts-ignore need to update package version or remove it*/}
      <Tabs {...tabProps} key={tabProps.tabs.length}>
        <Suspense fallback={<Fallback />}>
          <Bets {...betsProps} />
        </Suspense>
        {/*@Todo change this logic after types fixing*/}
        {(tabProps.page === 1 || tabProps.initialPage === 1) && (
          <Suspense>
            <OpenBets componentRid={''} />
          </Suspense>
        )}
      </Tabs>
    </ConditionalWrapper>
  );
});
